import { LiveDataRow } from "../models/LiveDataRow";
import { haystack } from "./diagnosticMatchers";

/** One line of the vehicle checklist: was this value reported, and what was it. */
export interface CheckItem {
  label: string;
  value: string;
  found: boolean;
  /** The value came from the last 24 hours rather than from the live window. */
  fromFallback: boolean;
}

export type RowPredicate = (row: LiveDataRow) => boolean;

/** How to recognise the reading a check is asking about. */
export type RowMatch = RowPredicate | RegExp;

export type ValueFormatter = (row: LiveDataRow | undefined) => string;

/**
 * The newest reading that matches.
 *
 * By timestamp rather than by array position: readings are paged and merged concurrently, so
 * the order they arrive in says nothing about which is newest.
 */
function latestMatching(rows: LiveDataRow[], predicate: RowPredicate): LiveDataRow | undefined {
  let newest: LiveDataRow | undefined;

  for (const row of rows) {
    if (!predicate(row)) {
      continue;
    }
    if (newest === undefined || row.dateTime.getTime() > newest.dateTime.getTime()) {
      newest = row;
    }
  }

  return newest;
}

export interface CreateCheckItemInput {
  label: string;
  /** Readings from the live window. */
  rows: LiveDataRow[];
  /** Readings from the wider history, used only when the live window has nothing. */
  fallbackRows: LiveDataRow[];
  match: RowMatch;
  format: ValueFormatter;
}

/**
 * Answers one check from the readings.
 *
 * A vehicle parked overnight reports nothing live, so the check falls back to the history — but
 * says which it used, because "reported an hour ago" and "reported yesterday" mean different
 * things to someone verifying an installation.
 */
export function createCheckItem(input: CreateCheckItemInput): CheckItem {
  const { label, rows, fallbackRows, match, format } = input;
  const predicate = toPredicate(match);

  const live = latestMatching(rows, predicate);
  const fallback = live ? undefined : latestMatching(fallbackRows, predicate);
  const value = format(live ?? fallback);

  // A reading that matched but cannot be shown -- a value that is not a finite number, say -- is
  // not an answer. Counting it as found would put a tick next to the words "not found".
  const found = value !== "";

  return { label, value, found, fromFallback: found && live === undefined };
}

function toPredicate(match: RowMatch): RowPredicate {
  return typeof match === "function" ? match : row => match.test(haystack(row));
}
